import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { Icon } from '../Icon';
import { Spinner } from '../Spinner';

import type { Theme } from '../../theme';
import type { FileProgressStatus } from '../../types/riskReview';
import type { IconColor } from '../Icon';
import type { ReviewProcessingProps } from './ReviewProcessing.types';

/**
 * Gets the semantic color from theme for a file progress status.
 * Uses theme.colors.semantic for consistency with Icon component colors.
 */
const getStatusColor = (status: FileProgressStatus, theme: Theme): string => {
  switch (status) {
    case 'completed':
      return theme.colors.semantic.green.base;
    case 'failed':
      return theme.colors.semantic.red.base;
    case 'processing':
    case 'uploading':
      return theme.colors.semantic.orange.base;
    case 'pending':
    default:
      return theme.colors.grey[400];
  }
};

/**
 * Gets the IconColor for a file progress status.
 * Maps to Icon component's color prop values.
 */
const getStatusIconColor = (status: FileProgressStatus): IconColor => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
    case 'uploading':
      return 'warning';
    case 'pending':
    default:
      return 'muted';
  }
};

/**
 * Gets the icon name for a file progress status
 */
const getStatusIcon = (status: FileProgressStatus): string => {
  switch (status) {
    case 'completed':
      return 'check_circle';
    case 'failed':
      return 'error';
    case 'processing':
    case 'uploading':
      return 'sync';
    case 'pending':
    default:
      return 'schedule';
  }
};

/**
 * Gets the status display text
 */
const getStatusText = (status: FileProgressStatus, progress?: number): string => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'uploading':
      return progress !== undefined ? `Uploading ${progress}%` : 'Uploading...';
    case 'processing':
      return 'Processing...';
    case 'pending':
    default:
      return 'Pending';
  }
};

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h2`
  ${({ theme }) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.text.primary};
    margin: 0;
    text-align: center;
  `}
`;

const Message = styled.p`
  ${({ theme }) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.md};
    color: ${theme.colors.text.primary};
    margin: 0;
    text-align: center;
  `}
`;

const HelperText = styled.p`
  ${({ theme }) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.text.secondary};
    margin: 0;
    text-align: center;
  `}
`;

const ProgressSummary = styled.div`
  ${({ theme }) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.secondary};
    margin-top: 8px;
    text-align: center;
  `}
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 8px;
`;

const FileItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background-color: ${theme.colors.grey[100]};
    border-radius: 4px;
    font-family: ${theme.typography.fontFamily};
  `}
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const FileName = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

interface StatusTextStyleProps {
  $status: FileProgressStatus;
}

const StatusTextStyled = styled.span<StatusTextStyleProps>`
  ${({ theme, $status }) => css`
    font-size: ${theme.typography.fontSize.xs};
    color: ${getStatusColor($status, theme)};
  `}
`;

const StatusIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressBar = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 4px;
    background-color: ${theme.colors.grey[200]};
    border-radius: 2px;
    overflow: hidden;
    margin-top: 4px;
  `}
`;

interface ProgressFillProps {
  $progress: number;
}

const ProgressFill = styled.div<ProgressFillProps>`
  ${({ theme, $progress }) => css`
    height: 100%;
    background-color: ${theme.colors.semantic.orange.base};
    border-radius: 2px;
    width: ${$progress}%;
    transition: width 0.3s ease;
  `}
`;

/**
 * ReviewProcessing Component
 *
 * Display processing/loading state during review with support for single and multi-file modes.
 *
 * @example
 * ```tsx
 * // Single file mode
 * <ReviewProcessing
 *   mode="single"
 *   isVisible={isProcessing}
 *   message="Uploading file..."
 *   dataTestId="review-processing"
 * />
 *
 * // Multi-file mode
 * <ReviewProcessing
 *   mode="multiple"
 *   isVisible={isProcessing}
 *   progress={{
 *     totalFiles: 3,
 *     completedFiles: 1,
 *     failedFiles: 0,
 *     files: [
 *       { fileId: '1', fileName: 'doc.pdf', status: 'completed' },
 *       { fileId: '2', fileName: 'report.pdf', status: 'processing' },
 *       { fileId: '3', fileName: 'image.png', status: 'pending' }
 *     ]
 *   }}
 *   dataTestId="multi-file-processing"
 * />
 * ```
 */
export const ReviewProcessing: React.FC<ReviewProcessingProps> = ({
  mode,
  progress,
  message = 'Processing Risk Review...',
  helperText = 'This may take a few moments',
  title,
  isVisible,
  dataTestId,
  dataId,
  className,
  style,
}) => {
  const theme = useTheme() as Theme;
  const warningColor = theme.colors.semantic.orange.base;

  if (!isVisible) {
    return null;
  }

  const renderSingleMode = () => (
    <>
      <Spinner size="lg" />
      {title && <Title>{title}</Title>}
      <Message>{message}</Message>
      <HelperText>{helperText}</HelperText>
    </>
  );

  const renderMultipleMode = () => {
    if (!progress) {
      return renderSingleMode();
    }

    const { totalFiles, completedFiles, failedFiles, files } = progress;
    const inProgressCount = totalFiles - completedFiles - failedFiles;

    return (
      <>
        <Spinner size="lg" />
        {title && <Title>{title}</Title>}
        <Message>
          Processing {totalFiles} file{totalFiles !== 1 ? 's' : ''}...
        </Message>
        <ProgressSummary>
          {completedFiles} completed
          {failedFiles > 0 && `, ${failedFiles} failed`}
          {inProgressCount > 0 && `, ${inProgressCount} remaining`}
        </ProgressSummary>
        <FileList data-testid={dataTestId ? `${dataTestId}-file-list` : undefined}>
          {files.map((file) => {
            const isInProgress = file.status === 'processing' || file.status === 'uploading';

            return (
              <FileItem
                key={file.fileId}
                data-testid={dataTestId ? `${dataTestId}-file-${file.fileId}` : undefined}
              >
                <StatusIconContainer>
                  {isInProgress ? (
                    <Spinner size="xs" variant="dots" customColor={warningColor} />
                  ) : (
                    <Icon
                      name={getStatusIcon(file.status)}
                      size="sm"
                      color={getStatusIconColor(file.status)}
                    />
                  )}
                </StatusIconContainer>
                <FileInfo>
                  <FileName title={file.fileName}>{file.fileName}</FileName>
                  <StatusTextStyled $status={file.status}>
                    {file.error || file.message || getStatusText(file.status, file.progress)}
                  </StatusTextStyled>
                  {file.status === 'uploading' && file.progress !== undefined && (
                    <ProgressBar>
                      <ProgressFill $progress={file.progress} />
                    </ProgressBar>
                  )}
                </FileInfo>
              </FileItem>
            );
          })}
        </FileList>
      </>
    );
  };

  return (
    <Overlay
      data-testid={dataTestId}
      data-id={dataId}
      className={className}
      style={style}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={title || message}
    >
      <ContentContainer>
        {mode === 'single' ? renderSingleMode() : renderMultipleMode()}
      </ContentContainer>
    </Overlay>
  );
};

ReviewProcessing.displayName = 'ReviewProcessing';

export default ReviewProcessing;
